import { useContext, useEffect, useRef, useState } from 'react'

import { TelemetryContext } from '../context/TelemetryContext'
import { STUDY_EVENTS } from '../constants/telemetryEvents'
import CodeBlock from '../../../study/components/CodeBlock'
import {
  idKey,
  newClientEventId,
  readPendingAnswer,
  readShownAt,
  sendAnswerKeepalive,
  sendUntilSaved,
  storePendingAnswer,
  storeShownAt,
} from './probeService'

import '../css/post-survey.scss'
import './probe-modal.scss'

// The concept probes after a study condition's coding time (useStudyPhase's 'probes'), on the
// workspace survey overlay: one question per screen, in order, forward only, and never a word
// on whether an answer was right - the keys stay on the server until the last answer, when
// the Thank You overlay shows them (ProbeReview). Workspace makes everything underneath inert
// meanwhile, so there is no editing, Run or AI chat.
//
// Each answer is kept in this browser and sent at once, then again until the server has it
// (probeService.sendUntilSaved); the student moves on without waiting. If the page goes away
// first, what is not confirmed goes out once more with keepalive. After the last question the
// student is asked to keep the page open, with no thanks yet: those wait for the server. Once
// it has every answer, useStudyPhase turns to 'done' and Workspace shows its Thank You overlay.

// `code` spans in question and option text, shown as code the way the study pages' RichText
// does. Nothing else in the text is markup. Also ProbeReview's.
export const withInlineCode = (text) =>
  text.split(/(`[^`]+`)/g).map((part, index) =>
    part.length > 2 && part.startsWith('`') && part.endsWith('`')
      ? <code key={index} className="probe-ic">{part.slice(1, -1)}</code>
      : part
  )

// passedAll: the probes started because the student's tests all passed, not because the time
// is up (useStudyPhase's finishedEarly), which the headings say.
export default function ProbeModal({ sessionId, userId, probes, onSaved, passedAll = false }) {
  const dialogRef = useRef(null)

  // Focus moves into the dialog: the workspace underneath is inert, and a key press must not
  // go to an editor the student cannot see.
  useEffect(() => {
    dialogRef.current?.focus()
  }, [])

  return (
    <div className="survey-overlay probe-overlay">
      <div
        ref={dialogRef}
        className="survey-modal probe-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="probe-title"
        tabIndex={-1}
      >
        {probes.status === 'ready' ? (
          <ProbeQuestions
            sessionId={sessionId}
            userId={userId}
            items={probes.items}
            savedIds={probes.savedIds}
            onSaved={onSaved}
            passedAll={passedAll}
          />
        ) : (
          <div className="survey-thankyou">
            <h2 id="probe-title">{passedAll ? 'All tests passed' : 'Time is up'}</h2>
            <p>{probes.failed ? 'Still loading the questions. Please keep this page open.' : 'Loading the questions...'}</p>
          </div>
        )}
      </div>
    </div>
  )
}

// Mounted once the questions are loaded, so what an earlier page load kept is read before the
// first question renders.
function ProbeQuestions({ sessionId, userId, items, savedIds, onSaved, passedAll }) {
  const { logTelemetryEvent } = useContext(TelemetryContext)

  // Answers an earlier page load kept but had not sent count as given - the student does not
  // see those questions again - and are sent again on mount.
  const [kept] = useState(() =>
    items
      .filter((item) => !savedIds.has(idKey(item.id)))
      .map((item) => readPendingAnswer(sessionId, userId, item.id))
      .filter(Boolean)
  )
  const [answered, setAnswered] = useState(() => new Set(kept.map((answer) => idKey(answer.itemId))))
  const [choice, setChoice] = useState(null)
  // When each item first appeared: shownAt for the server, and the performance.now() reading
  // that rtMs is measured from when that was on this page (null when it was on an earlier
  // page load).
  const shownRef = useRef({})
  const mountedRef = useRef(true)
  // The answers sent and not yet confirmed, by item: the stored copies, and still there when
  // this browser cannot store them (private windows).
  const pendingRef = useRef(new Map())

  useEffect(() => () => {
    mountedRef.current = false
  }, [])

  const send = (answer) => {
    pendingRef.current.set(idKey(answer.itemId), answer)
    sendUntilSaved(sessionId, answer, { isCancelled: () => !mountedRef.current }).then((saved) => {
      if (!saved) return
      pendingRef.current.delete(idKey(answer.itemId))
      if (mountedRef.current) onSaved(saved)
    })
  }

  useEffect(() => {
    kept.forEach(send)
    // Once, on mount: `kept` never changes, and each answer's retries run until it is saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The page may be going away with answers unconfirmed: pagehide covers closing and
  // reloading, and a hidden tab is the last moment a page is sure to run on a phone. Each goes
  // out once more with keepalive, which the browser finishes after the page is gone (the
  // axios retries would be cancelled); a hidden tab that comes back keeps retrying as before.
  useEffect(() => {
    const sendPending = () => pendingRef.current.forEach((answer) => sendAnswerKeepalive(sessionId, answer))
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') sendPending()
    }
    window.addEventListener('pagehide', sendPending)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('pagehide', sendPending)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [sessionId])

  const remaining = items.filter((item) => !savedIds.has(idKey(item.id)) && !answered.has(idKey(item.id)))
  const current = remaining[0] || null

  useEffect(() => {
    if (!current || shownRef.current[idKey(current.id)]) return
    // Shown before this page load (a reload in the middle of the question): its time runs on
    // from then, so the reload does not restart it.
    const shownBefore = readShownAt(sessionId, userId, current.id)
    if (shownBefore) {
      shownRef.current[idKey(current.id)] = { shownAt: shownBefore, shownPerf: null }
      logTelemetryEvent(STUDY_EVENTS.PROBE_SHOWN, { itemId: current.id, resumed: true })
      return
    }
    const shownAt = new Date().toISOString()
    shownRef.current[idKey(current.id)] = { shownAt, shownPerf: performance.now() }
    storeShownAt(sessionId, userId, current.id, shownAt)
    logTelemetryEvent(STUDY_EVENTS.PROBE_SHOWN, { itemId: current.id })
  }, [current, logTelemetryEvent, sessionId, userId])

  const handleNext = () => {
    if (!current || !choice) return
    const shown = shownRef.current[idKey(current.id)] || { shownAt: new Date().toISOString(), shownPerf: performance.now() }
    const answeredAtMs = Date.now()
    const answer = {
      userId,
      itemId: current.id,
      choice,
      shownAt: shown.shownAt,
      answeredAt: new Date(answeredAtMs).toISOString(),
      // performance.now() when the item was first shown on this page: a change to the
      // computer's clock meanwhile does not move it. Across a reload only the clock can tell
      // (performance.now() starts over with each page); a clock set back in between makes it
      // negative, which the server stores as unknown.
      rtMs:
        shown.shownPerf !== null
          ? Math.round(performance.now() - shown.shownPerf)
          : answeredAtMs - Date.parse(shown.shownAt),
      clientEventId: newClientEventId(),
    }
    // Stored before it is sent, so a reload a moment later cannot lose it.
    storePendingAnswer(sessionId, answer)
    logTelemetryEvent(STUDY_EVENTS.PROBE_ANSWERED, { itemId: answer.itemId, choice: answer.choice, rtMs: answer.rtMs })
    if (remaining.length === 1) logTelemetryEvent(STUDY_EVENTS.PROBES_COMPLETED, {})
    setAnswered((previous) => new Set(previous).add(idKey(current.id)))
    setChoice(null)
    send(answer)
  }

  // Every item answered. Until the server has every answer the student is asked to keep the
  // page open - no check mark, no thanks - since closing it now could lose one. Workspace
  // swaps in its Thank You overlay as soon as the server has them all, with the answers.
  if (!current) {
    return items.every((item) => savedIds.has(idKey(item.id))) ? (
      <div className="survey-thankyou">
        <div className="survey-check">✓</div>
        <h2 id="probe-title">Thank You!</h2>
        <p>Your answers have been saved.</p>
      </div>
    ) : (
      <div className="survey-thankyou">
        <h2 id="probe-title">Saving your answers - please keep this page open</h2>
      </div>
    )
  }

  return (
    <>
      <div className="survey-header">
        <h2 id="probe-title">Concept Questions</h2>
        <p>
          {passedAll ? 'All tests passed.' : 'The coding time is up.'} Answer each question on your own; you cannot go
          back to one once you move on.
        </p>
      </div>

      {/* Keyed by item, so each question starts scrolled to the top. */}
      <div className="survey-questions" key={idKey(current.id)}>
        <div className="survey-question probe-question">
          <span className="probe-count">
            Question {items.indexOf(current) + 1} of {items.length}
          </span>
          {current.blocks.map((block, index) =>
            block.type === 'code' ? (
              <CodeBlock key={index} code={block.code} lang="python" />
            ) : (
              <p key={index} className="probe-text">{withInlineCode(block.text)}</p>
            )
          )}
          <div className="probe-options">
            {current.options.map((option) => (
              <button
                key={option.letter}
                type="button"
                aria-pressed={choice === option.letter}
                className={`probe-option${choice === option.letter ? ' selected' : ''}`}
                onClick={() => setChoice(option.letter)}
              >
                <span className="probe-letter">{option.letter}</span>
                <span>{withInlineCode(option.text)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="survey-footer">
        <button type="button" className="survey-submit" onClick={handleNext} disabled={!choice}>
          {remaining.length === 1 ? 'Submit' : 'Next'}
        </button>
      </div>
    </>
  )
}
